<template>
	<PageContainer title="Auto escolas" data-testid="page-container-comercial">
		<Box space="medium" class="autoescola-container" data-testid="comercial-container">
			<form @submit.prevent="handleSubmit" data-testid="comercial-form">
				<div class="inputs" data-testid="form-inputs">
					<div class="inputs-row" data-testid="inputs-row">
						<Select :options="cfcsOptions" legend="Autoescola" placeholder="Selecione uma autoescola" :modelValue="cfc"
							@update:modelValue="$event => (this.cfc = $event)" :disabled="loadingCfc || loading" data-testid="select-autoescola" />

						<Select :options="vendedoresOptions" legend="Vendedor" placeholder="Digite o nome do vendedor"
							:modelValue="seller" @update:modelValue="$event => (this.seller = $event)"
							:disabled="loadingVendedor || loading" data-testid="select-vendedor" />
					</div>

					<div class="inputs-row" data-testid="inputs-row-tipo">
						<Select :options="tipoOptions" legend="Tipo" placeholder="Todos" :modelValue="tipo"
							@update:modelValue="alterarTipo" :disabled="loading" data-testid="select-tipo" />

						<!-- Vínculo com a empresa de monitoramento só existe para instrutor autônomo -->
						<Select v-if="tipo === 'autonomo'" :options="vinculoOptions" legend="Vínculo com o monitoramento"
							placeholder="Todos" :modelValue="vinculo" @update:modelValue="$event => (this.vinculo = $event)"
							:disabled="loading" data-testid="select-vinculo" />
						<div v-else class="filtro-vazio" aria-hidden="true"></div>
					</div>
				</div>

				<div class="buttons" data-testid="form-buttons">
					<Button type="button" @handle:click="clearInputs" text="Limpar" :fill="false" color="danger"
						:disabled="loading" data-testid="btn-limpar">
						<CloseCircleOutline :size="18" />
					</Button>

					<Button type="submit" text="Consultar" color="primary" :disabled="loading" data-testid="btn-consultar">
						<Magnify :size="18" />
					</Button>
				</div>
			</form>

			<div class="loading" v-if="loading" data-testid="loading-area">
				<LoadingSpinner />
			</div>

			<div v-else-if="autoescolas.length > 0 && !loading" class="table-container" data-testid="table-container">
				<Paginate :total-pages="paginationData.lastPage" :current-page="paginationData.currentPage"
					@page-changed="fetchCfcs" data-testid="paginate-autoescolas" />

				<Table :headers="['Nome fantasia', 'CNPJ/CPF', 'Monitoramento', 'Convênio com sindicato', 'Vendedor', 'Ações']" data-testid="autoescolas-table">
					<tr v-for="autoescola in autoescolas" :key="autoescola.id_cfc" :data-testid="'autoescola-row-' + autoescola.id_cfc">
						<td>
							<div class="nome">
								<span>{{ autoescola.nome_fantasia }}</span>
								<span v-if="isAutonomo(autoescola)" class="tag-autonomo">Autônomo</span>
							</div>
						</td>
						<td class="nowrap">{{ autoescola.cnpj }}</td>
						<td class="nowrap">
							<template v-if="isAutonomo(autoescola)">
								<Badge v-if="autoescola.vinculado_monitoramento" text="Vinculado" color="success"
									:title="`Vinculado em ${formatarData(autoescola.vinculado_monitoramento_em)} (informativo: o vínculo pode ter sido alterado no Detran)`" />
								<Badge v-else text="Não vinculado" color="warning" />
								<span v-if="autoescola.municipio_principal" class="municipio-principal"
									title="Município principal no Detran: define a regional de emissão de LADV">
									{{ autoescola.municipio_principal }}<template v-if="autoescola.codigo_regional"> · Regional {{ autoescola.codigo_regional }}</template>
								</span>
							</template>
							<span v-else class="nao-se-aplica" title="Vínculo exclusivo de instrutor autônomo">—</span>
						</td>
						<td>{{ autoescola.sindicato ? 'Sim' : 'Não' }}</td>
						<td v-if="autoescola.vendedor_atribuido">
							{{ autoescola.vendedor_atribuido.vendedor.nome }}
						</td>
						<td v-else>Sem Vendedor Associado</td>
						<td>
							<div class="actions">
								<div>
									<IconButton @handle:click="openInfoModal(autoescola.id_cfc)" color="secondary" title="Detalhes"
										:disabled="loadingActions" data-testid="btn-detalhes-autoescola">
										<InformationOutline :size="19" />
									</IconButton>
								</div>
								<div v-if='id_role == 1 || id_role == 8 || id_role == 2'>
									<IconButton @handle:click="() => openVendedoresModal(autoescola)" color="primary"
										title="Editar vendedores" :disabled="loadingActions" data-testid="btn-editar-vendedores">
										<AccountOutline :size="19" />
									</IconButton>
								</div>
								<div v-if="isAutonomo(autoescola) && canVincular">
									<IconButton @handle:click="openVincularModal(autoescola)"
										:color="autoescola.vinculado_monitoramento ? 'secondary' : 'primary'"
										:title="autoescola.vinculado_monitoramento ? 'Refazer vínculo com o monitoramento' : 'Vincular ao monitoramento'"
										:disabled="loadingActions" data-testid="btn-vincular-monitoramento">
										<LinkVariant v-if="autoescola.vinculado_monitoramento" :size="19" />
										<LinkVariantPlus v-else :size="19" />
									</IconButton>
								</div>
								<div>
									<IconButton @handle:click="openServicesModal(autoescola)" color="success" title="Serviços"
										:disabled="loadingActions" data-testid="btn-servicos">
										<TextBoxMultipleOutline :size="19" />
									</IconButton>
								</div>
								<template v-if='id_role == 1 || id_role == 2'>
									<div>
										<IconButton @handle:click="openEditCfcModal(autoescola)" color="primary" title="Editar auto escola"
											:disabled="loadingActions" data-testid="btn-editar-autoescola">
											<PencilOutline :size="19" />
										</IconButton>
									</div>
									<div>
										<IconButton @handle:click="openDeleteModal(autoescola.id_cfc)" color="danger"
											title="Excluir auto escola" :disabled="loadingActions" data-testid="btn-excluir-autoescola">
											<Close :size="19" />
										</IconButton>
									</div>
								</template>
							</div>
						</td>
					</tr>
				</Table>
			</div>
		</Box>

		<InfoModal ref="infoModal" data-testid="info-modal" />

		<VendedoresModal ref="vendedoresModal" :vendedores="vendedoresOptions" @update:vendedor="fetchCfcs"
			@update:gratuidade="fetchCfcs" data-testid="vendedores-modal" />

		<ServicesModal ref="servicesModal" data-testid="services-modal" />

		<VincularMonitoramentoModal ref="vincularModal" @vinculado="atualizarVinculo" />

		<EditCFCModal ref="editDialog" @cfcUpdated="fetchCfcs(paginationData.currentPage)" data-testid="edit-cfc-modal" />

		<ConfirmModal ref="confirmModal" title="Excluir CFC"
			message="Tem certeza que deseja excluir essa auto escola? esta ação é irreversível e a autoescola não poderá ser recuperada!"
			:onConfirm="excluirCFC" data-testid="modal-excluir-cfc" />
	</PageContainer>
</template>

<script>
import PageContainer from 'core/PageContainer'
import LoadingSpinner from 'core/LoadingSpinner'
import Box from 'core/Box'
import Select from 'core/Select'
import Button from 'core/Button'
import Table from 'core/Table'
import Paginate from 'core/Paginate'
import IconButton from 'core/IconButton'
import ConfirmModal from 'core/ConfirmModal'
import CloseCircleOutline from 'icons/CloseCircleOutline'
import Magnify from 'icons/Magnify'
import InformationOutline from 'icons/InformationOutline'
import PencilOutline from 'icons/PencilOutline'
import AccountOutline from 'icons/AccountOutline'
import Close from 'icons/Close'
import TextBoxMultipleOutline from 'icons/TextBoxMultipleOutline'
import LinkVariant from 'icons/LinkVariant'
import LinkVariantPlus from 'icons/LinkVariantPlus'
import Badge from 'core/Badge'

import InfoModal from './InfoModal'
import VendedoresModal from './VendedoresModal'
import ServicesModal from './ServicesModal'
import EditCFCModal from './EditCFCModal'
import VincularMonitoramentoModal from './VincularMonitoramentoModal'
import { isSchemaAutonomo } from '@/constants/userRoles'

import utils from '../../services/utils'

export default {
	name: 'indexComercial',
	components: {
		PageContainer,
		Box,
		LoadingSpinner,
		Select,
		Button,
		Table,
		Paginate,
		IconButton,
		ConfirmModal,

		CloseCircleOutline,
		Magnify,
		InformationOutline,
		PencilOutline,
		AccountOutline,
		Close,
		TextBoxMultipleOutline,
		LinkVariant,
		LinkVariantPlus,
		Badge,

		InfoModal,
		VendedoresModal,
		ServicesModal,
		EditCFCModal,
		VincularMonitoramentoModal,
	},
	data() {
		return {
			utils: utils,
			seller: '',
			cfc: '',
			tipo: '',
			vinculo: '',
			tipoOptions: [
				{ value: '', text: 'Todos' },
				{ value: 'autoescola', text: 'Autoescolas (CNPJ)' },
				{ value: 'autonomo', text: 'Instrutores autônomos (CPF)' },
			],
			vinculoOptions: [
				{ value: '', text: 'Todos' },
				{ value: 'vinculado', text: 'Vinculados' },
				{ value: 'nao_vinculado', text: 'Não vinculados' },
			],
			paginationData: {
				currentPage: 1,
				lastPage: 0,
				total: 0,
			},
			vendedoresOptions: [],
			cfcsOptions: [],
			autoescolas: [],
			loading: false,
			loadingCfc: false,
			loadingVendedor: false,
			loadingActions: false,
			id_role: localStorage.id_role,
			gratuidade: { 
				teorica: false, 
				teorica_inicio: null, 
				teorica_fim: null, 
				pratica: false, 
				pratica_inicio: null, 
				pratica_fim: null 
			},
		}
	},
	computed: {
		// Mesma regra de quem edita autoescola nesta tela
		canVincular() {
			return ['1', '2'].includes(String(this.id_role))
		},
	},
	async created() {
		await Promise.all([
			this.getConsultores(),
			this.getCfcs(),
		])
	},
	methods: {
		async getConsultores() {
			this.loadingVendedor = true

			await this.$http.get(`${process.env.VUE_APP_URL_API}/vendedores`)
				.then(response => {
					this.vendedoresOptions = response.body.dados.vendedores.map(vendedor => ({ value: vendedor.id, text: vendedor.nome }))
					this.vendedoresOptions.push({ value: 0, text: 'Autoescola sem vendedor atribuído' })
				})
				.catch(err => {
					this.$toasted.show('Erro ao buscar vendedores.', {
						icon: 'fa-times',
						position: 'top-right',
					})
				})
				.finally(() => {
					this.loadingVendedor = false
				})
		},
		async getCfcs() {
			this.loadingCfc = true

			await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/autoescola`)
				.then(response => {
					this.cfcsOptions = response.body.dados.map(cfc => ({ value: cfc.nome_fantasia, text: cfc.nome_fantasia }))
				})
				.catch(err => {
					this.$toasted.show('Erro ao buscar autoescolas.', {
						icon: 'fa-times',
						position: 'top-right',
					})
				})
				.finally(() => {
					this.loadingCfc = false
				})
		},
		async handleSubmit() {
			await this.fetchCfcs()
		},
		async fetchCfcs(page = 1) {
			this.loading = true

			const params = {
				page,
				nomeFantasia: this.cfc,
			}

			if (this.seller) params.vendedor = this.seller
			if (this.tipo) params.tipo = this.tipo
			if (this.tipo === 'autonomo' && this.vinculo) params.vinculo = this.vinculo

			await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/autoescola/consultar/nome+fantasia`, {
				params,
			})
				.then(response => {
					const dados = response.body.dados

					if (dados.data.length < 1) {
						this.$toasted.show(`Sem resultados para a sua pesquisa.`, {
							icon: 'fa-times',
							position: 'top-right',
						})
					}

					this.autoescolas = dados.data
					this.paginationData = {
						currentPage: dados.current_page,
						lastPage: dados.last_page,
						total: dados.total,
					}
				})
				.catch(err => {
					this.$toasted.show(`Erro ao buscar autoescolas: ${err.body.mensagem}`, {
						icon: 'fa-times',
						position: 'top-right',
					})
				})
				.finally(() => {
					this.loading = false
				})
		},
		openInfoModal(cfcId) {
			this.$refs.infoModal.open(cfcId)
		},
		clearInputs() {
			this.seller = ''
			this.cfc = ''
			this.tipo = ''
			this.vinculo = ''
		},
		alterarTipo(tipo) {
			this.tipo = tipo
			if (tipo !== 'autonomo') this.vinculo = ''
		},
		isAutonomo(autoescola) {
			return isSchemaAutonomo(autoescola.tenant_schema) || (autoescola.cnpj || '').replace(/\D/g, '').length === 11
		},
		formatarData(data) {
			// Laravel 6 serializa como "Y-m-d H:i:s"
			return data ? utils.formatOnlyDate(String(data).slice(0, 10)) : '-'
		},
		openVincularModal(autoescola) {
			this.$refs.vincularModal.open(autoescola)
		},
		// Atualiza só a linha: a resposta não traz as relações (vendedor, serviços) da listagem
		atualizarVinculo(atualizada) {
			const autoescola = this.autoescolas.find(item => item.id_cfc === atualizada.id_cfc)
			if (!autoescola) return

			autoescola.vinculado_monitoramento = atualizada.vinculado_monitoramento
			autoescola.vinculado_monitoramento_em = atualizada.vinculado_monitoramento_em
			autoescola.municipio_principal = atualizada.municipio_principal
			autoescola.codigo_municipio_principal = atualizada.codigo_municipio_principal
			autoescola.codigo_regional = atualizada.codigo_regional
		},
		openVendedoresModal(autoescola) {
			this.$refs.vendedoresModal.open(autoescola)
		},
		openServicesModal(autoescola) {
			this.$refs.servicesModal.open(autoescola)
		},
		openEditCfcModal(cfc) {
			this.$refs.editDialog.open(cfc)
		},
		openDeleteModal(id) {
			this.$refs.confirmModal.open(id)
		},
		excluirCFC(id) {
			this.$http.delete(`${process.env.VUE_APP_URL_API}/cfc/excluir/${id}`).then(this.callbackDelete)
		},
		callbackDelete(response) {
			if (response.data.sucesso) {
				this.$toasted.show('Autoescola Deletada Com Sucesso!', { position: 'top-right' })
				this.$refs.confirmModal.close()
				this.cfc = null
				this.fetchCfcs()
			} else {
				this.$refs.confirmModal.close()
				this.$toasted.show('Ops! Não foi possível excluir Autoescola.', {
					position: 'top-right'
				})
			}
		},
	},
}
</script>

<style lang="scss" scoped>
.loading {
	display: flex;
	justify-content: center;
	align-items: center;
}

.autoescola-container {
	display: flex;
	flex-direction: column;
	gap: 2.25rem;

	form {
		display: flex;
		flex-direction: column;
		gap: 2.5rem;

		.inputs {
			display: flex;
			flex-direction: column;
			gap: 1.5rem;

			.inputs-row {
				display: flex;
				flex-wrap: wrap;
				gap: 1.5rem;

				>div {
					flex: 1;
				}
			}
		}

		.buttons {
			align-self: flex-end;

			display: flex;
			gap: 0.5rem;
		}
	}

	@media (max-width: 576px) {
		form {
			.inputs .inputs-row {
				> div {
					flex-basis: 100%;
				}

				.filtro-vazio {
					display: none;
				}
			}

			.buttons {
				align-self: stretch;

				> * {
					flex: 1;
				}
			}
		}
	}

	.table-container {
		display: flex;
		flex-direction: column;
		gap: 1rem;

		.nome {
			display: flex;
			flex-wrap: wrap;
			align-items: center;
			gap: 0.375rem 0.5rem;
		}

		.tag-autonomo {
			padding: 0.125rem 0.5rem;
			border: 1px solid rgba(28, 45, 90, 0.25);
			border-radius: 999px;
			background-color: rgba(28, 45, 90, 0.05);
			color: #1c2d5a;
			font-size: 0.6875rem;
			font-weight: 700;
			white-space: nowrap;
		}

		.nowrap {
			white-space: nowrap;
		}

		.nao-se-aplica {
			color: #b0b0b0;
		}

		.municipio-principal {
			display: block;
			margin-top: 0.25rem;
			font-size: 0.75rem;
			color: #606264;
		}

		.pagination-container {
			align-self: flex-end;
		}

		.table-container {
			.actions {
				display: flex;
				align-items: center;

				div {
					padding: 0 0.5rem;
					display: flex;
					justify-content: center;

					&:not(:first-child) {
						border-left: 1px solid #ECECEC;
					}

					&:not(:last-child) {
						border-right: 1px solid #ECECEC;
					}
				}
			}
		}
	}
}
</style>
